import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';

import { useEnv } from '../../context/EnvContext';
import { useActiveTenantEditable } from '../../context/useActiveTenantEditable';
import { READONLY_INTERACTIVE_ATTR } from '../../context/ReadOnlyGuard';
import { colors } from '../../theme/tokens';
import EnvPill from '../../components/ui/EnvPill';

/**
 * CrossEnvBanner — graceful "Couldn't reach {env}" notice.
 *
 * When you view a REMOTE env, every read is proxied through the home backend's
 * env-proxy to that env's signed read-gateway. If the target env is down /
 * mid-deploy / unreachable, those reads return 502 ENV_PROXY_UPSTREAM and pages
 * would otherwise each show a bare "failed to load". axiosClient emits an
 * `env-proxy-status` event on proxied reads; this banner shows one clear
 * message + an escape hatch back to a reachable env.
 *
 * Resilience contract (G4 C1):
 *  - axiosClient ONLY emits reachable=false on 502 ENV_PROXY_UPSTREAM or a
 *    no-response network failure — NOT on 404/403/4xx business responses.
 *    So this banner will never fire for "tenant not found" or "access denied".
 *  - Last-event-wins: a successful read after a transient failure clears it.
 *  - Navigating away from the unreachable env clears it immediately.
 */
const CrossEnvBanner: React.FC = () => {
  const { currentEnv, setCurrentEnv, editableEnv } = useEnv();
  const { homeEnv } = useActiveTenantEditable();
  const [unreachableEnv, setUnreachableEnv] = React.useState<string | null>(null);

  React.useEffect(() => {
    const onStatus = (e: Event): void => {
      const d = (e as CustomEvent<{ env: string; reachable: boolean }>).detail;
      // Only act on events that carry both env and an explicit reachable flag.
      // 404/403 business responses never set reachable=false in axiosClient
      // (G4 contract) so we won't get stuck on a single auth/not-found failure.
      if (!d || !d.env) return;
      setUnreachableEnv((prev) => {
        if (d.reachable) return prev === d.env ? null : prev; // recovered → clear
        return d.env; // true outage (502 / no-response) → show banner
      });
    };
    window.addEventListener('env-proxy-status', onStatus as EventListener);
    return () => window.removeEventListener('env-proxy-status', onStatus as EventListener);
  }, []);

  // Clear stale state once the user navigates away from the unreachable env.
  React.useEffect(() => {
    if (unreachableEnv && currentEnv !== unreachableEnv) setUnreachableEnv(null);
  }, [currentEnv, unreachableEnv]);

  if (!unreachableEnv || unreachableEnv !== currentEnv) return null;

  // Prefer the active tenant's home, else the env-level editable env, as the
  // "known good" env to bounce back to.
  const back = homeEnv && homeEnv !== currentEnv ? homeEnv : editableEnv;

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="cross-env-unreachable-banner"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 4,
        py: 1.25,
        backgroundColor: colors.errorBgStrong,
        borderBottom: `1px solid ${colors.errorBorder}`,
      }}
    >
      <CloudOffOutlinedIcon sx={{ color: colors.errorIcon, width: 20, height: 20 }} />
      <Typography
        // Render as a <div>: this message embeds an EnvPill (a <div>), which is
        // invalid DOM nesting inside the default <p> and emits a React
        // validateDOMNesting console.error (harmless visually, but it trips
        // react-scripts' fail-on-console-error under some test orderings).
        component="div"
        sx={{
          fontFamily: 'Manrope, sans-serif',
          fontSize: 13,
          fontWeight: 600,
          color: colors.errorText,
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexWrap: 'wrap',
        }}
      >
        Couldn&apos;t reach
        <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
          <EnvPill env={currentEnv} size="sm" />
        </Box>
        — it may be deploying or unavailable. Some data on this page is missing.
      </Typography>
      {back && back !== currentEnv && (
        <span {...{ [READONLY_INTERACTIVE_ATTR]: 'true' }}>
          <Button
            size="small"
            variant="outlined"
            data-testid="cross-env-switch-back"
            onClick={() => setCurrentEnv(back)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 12,
              borderColor: colors.errorIcon,
              color: colors.errorText,
              '&:hover': { borderColor: colors.errorText, backgroundColor: 'transparent' },
            }}
          >
            Back to {back}
          </Button>
        </span>
      )}
    </Box>
  );
};

export default CrossEnvBanner;
